import { proxyFetchGet } from '@/api/http';
import type { BotModelsPayload } from '@/lib/use-bot-service';
import {
  Globe,
  MessageCircle,
  MessageSquare,
  Phone,
  Send,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

/** Where the setup wizard and its links return to in the new settings. */
export const GATEWAY_URL = '/home?section=settings&tab=gateway';
export const CHANNELS_URL = '/home?section=settings&tab=channels';

export type SetupChannelMode = 'shared' | 'own';

export interface SetupChannelField {
  key: string;
  label: string;
  placeholder: string;
  required?: boolean;
  secret?: boolean;
}

export interface SetupChannelOption {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  color: string;
  modes: SetupChannelMode[];
  tokenFields: SetupChannelField[];
  requiresToken: boolean;
}

export interface OpenClawChannelConfig {
  mode?: string;
  botToken?: string;
  signingSecret?: string;
  appToken?: string;
  enabled?: boolean;
  [key: string]: unknown;
}

export interface OpenClawSetupSummary {
  channelCount: number;
  modelCount: number;
  botStatus: string;
}

export interface OpenClawSetupStatus {
  loading: boolean;
  error: string | null;
  channels: Record<string, OpenClawChannelConfig>;
  botStatus: string;
  models: BotModelsPayload | null;
  hasConfiguredChannels: boolean;
  hasConfiguredModels: boolean;
  hasMeaningfulSetup: boolean;
  summary: OpenClawSetupSummary;
  refresh: () => Promise<void>;
}

export const OPENCLAW_SETUP_CHANNELS: SetupChannelOption[] = [
  {
    id: 'telegram',
    name: 'Telegram',
    description: 'Use the shared Hanggent bot or bring your own Telegram bot.',
    icon: Send,
    color: 'bg-blue-500',
    modes: ['shared', 'own'],
    requiresToken: false,
    tokenFields: [
      {
        key: 'botToken',
        label: 'Bot token',
        placeholder: 'Enter your Telegram bot token from BotFather',
        required: true,
        secret: true,
      },
    ],
  },
  {
    id: 'discord',
    name: 'Discord',
    description: 'Connect your own Discord bot token.',
    icon: MessageCircle,
    color: 'bg-indigo-500',
    modes: ['own'],
    requiresToken: true,
    tokenFields: [
      {
        key: 'botToken',
        label: 'Bot token',
        placeholder: 'Enter your Discord bot token',
        required: true,
        secret: true,
      },
    ],
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Connect a Slack app with bot token and signing secret.',
    icon: MessageSquare,
    color: 'bg-purple-500',
    modes: ['own'],
    requiresToken: true,
    tokenFields: [
      {
        key: 'botToken',
        label: 'Bot token',
        placeholder: 'xoxb-...',
        required: true,
        secret: true,
      },
      {
        key: 'signingSecret',
        label: 'Signing secret',
        placeholder: 'Enter your Slack signing secret',
        secret: true,
      },
      {
        key: 'appToken',
        label: 'App token',
        placeholder: 'xapp-... (optional)',
        secret: true,
      },
    ],
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    description: 'Prepare WhatsApp pairing now, then scan the QR code after startup.',
    icon: Phone,
    color: 'bg-green-500',
    modes: ['own'],
    requiresToken: false,
    tokenFields: [],
  },
  {
    id: 'webchat',
    name: 'Web Chat',
    description: 'Start with the built-in web chat and add external channels later.',
    icon: Globe,
    color: 'bg-teal-500',
    modes: ['own'],
    requiresToken: false,
    tokenFields: [],
  },
];

export const OPENCLAW_CLOUD_PROVIDERS = [
  ...(import.meta.env.VITE_USE_LOCAL_PROXY !== 'true'
    ? [{ id: 'hanggent', label: 'Hanggent Cloud' }]
    : []),
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'google', label: 'Google' },
  { id: 'deepseek', label: 'DeepSeek' },
  { id: 'qwen', label: 'Qwen' },
  { id: 'minimax', label: 'Minimax' },
  { id: 'kimi', label: 'KIMI' },
  { id: 'z-ai', label: 'Z.ai' },
  { id: 'xai', label: 'xAI' },
  { id: 'groq', label: 'Groq' },
  { id: 'together', label: 'Together AI' },
  { id: 'openrouter', label: 'OpenRouter' },
];

export const OPENCLAW_LOCAL_PLATFORMS = [
  { id: 'ollama', label: 'Ollama' },
  { id: 'vllm', label: 'vLLM' },
  { id: 'sglang', label: 'SGLang' },
  { id: 'lmstudio', label: 'LM Studio' },
];

export const OPENCLAW_LOCAL_DEFAULT_ENDPOINTS: Record<string, string> = {
  ollama: 'http://localhost:11434',
  vllm: 'http://localhost:8000/v1',
  sglang: 'http://localhost:30000/v1',
  lmstudio: 'http://localhost:1234/v1',
};

// ── Gateway configuration ────────────────────────────────────────────────────

export type GatewayBindMode = 'loopback' | 'lan' | 'auto' | 'custom';

export interface GatewayDraft {
  port: number;
  bindMode: GatewayBindMode;
  customBind: string;
}

export const OPENCLAW_GATEWAY_BIND_MODES: Array<{
  id: GatewayBindMode;
  label: string;
  description: string;
}> = [
  { id: 'loopback', label: 'Loopback only', description: '127.0.0.1 — local machine only' },
  { id: 'lan', label: 'LAN', description: '0.0.0.0 — accessible from the local network' },
  { id: 'auto', label: 'Auto', description: 'Let OpenClaw choose automatically' },
  { id: 'custom', label: 'Custom', description: 'Specify an IP address to bind to' },
];

export const DEFAULT_GATEWAY_DRAFT: GatewayDraft = {
  port: 18789,
  bindMode: 'loopback',
  customBind: '',
};

// ── Web tools configuration ──────────────────────────────────────────────────

export type WebSearchProviderId = 'perplexity' | 'brave';

// ── Tools profile configuration ──────────────────────────────────────────────

export type ToolsProfile = 'messaging' | 'coding' | 'full';

export const OPENCLAW_TOOLS_PROFILES: Array<{
  id: ToolsProfile;
  label: string;
  description: string;
}> = [
  { id: 'messaging', label: 'Messaging', description: 'Chat only — no system tools' },
  { id: 'coding', label: 'Coding', description: 'Includes sandbox, browser, and exec tools' },
  { id: 'full', label: 'Full', description: 'All available tools enabled' },
];

export interface WebToolsDraft {
  searchEnabled: boolean;
  searchProvider: WebSearchProviderId;
  searchApiKey: string;
  fetchEnabled: boolean;
}

export const OPENCLAW_WEB_SEARCH_PROVIDERS: Array<{
  id: WebSearchProviderId;
  label: string;
}> = [
  { id: 'perplexity', label: 'Perplexity' },
  { id: 'brave', label: 'Brave Search' },
];

export const DEFAULT_WEB_TOOLS_DRAFT: WebToolsDraft = {
  searchEnabled: false,
  searchProvider: 'perplexity',
  searchApiKey: '',
  fetchEnabled: true,
};

// ── Advanced config drafts ───────────────────────────────────────────────────

export interface AdvancedDraft {
  toolsProfile: ToolsProfile;
  acpEnabled: boolean;
}

export const DEFAULT_ADVANCED_DRAFT: AdvancedDraft = {
  toolsProfile: 'messaging',
  acpEnabled: false,
};

// ── Config patch builders ────────────────────────────────────────────────────

const BIND_ADDRESS_MAP: Record<GatewayBindMode, string | undefined> = {
  loopback: '127.0.0.1',
  lan: '0.0.0.0',
  auto: undefined,
  custom: undefined,
};

export function buildGatewayConfigPatch(draft: GatewayDraft) {
  const gateway: Record<string, unknown> = { port: draft.port };
  const bind = draft.bindMode === 'custom' ? draft.customBind.trim() : BIND_ADDRESS_MAP[draft.bindMode];
  if (bind) gateway.bind = bind;
  return { gateway };
}

export function buildWebToolsConfigPatch(draft: WebToolsDraft) {
  const search: Record<string, unknown> = { enabled: draft.searchEnabled };
  if (draft.searchEnabled) {
    search.provider = draft.searchProvider;
    if (draft.searchApiKey.trim()) search.apiKey = draft.searchApiKey.trim();
  }
  return {
    tools: {
      web: {
        search,
        fetch: { enabled: draft.fetchEnabled },
      },
    },
  };
}

export function buildWorkspaceConfigPatch(workspacePath: string) {
  const trimmed = workspacePath.trim();
  if (!trimmed) return {};
  return {
    agents: {
      defaults: {
        workspace: trimmed,
      },
    },
  };
}

export function buildAdvancedConfigPatch(draft: AdvancedDraft) {
  return {
    tools: {
      profile: draft.toolsProfile,
    },
    acp: {
      dispatch: {
        enabled: draft.acpEnabled,
      },
    },
  };
}

export const OPENCLAW_SECRET_KEYS = new Set(
  OPENCLAW_SETUP_CHANNELS.flatMap((channel) =>
    channel.tokenFields.filter((field) => field.secret).map((field) => field.key)
  )
);

export function getChannelOption(channelId: string | null) {
  if (!channelId) return null;
  return OPENCLAW_SETUP_CHANNELS.find((channel) => channel.id === channelId) ?? null;
}

export function hasConfiguredChannels(
  channels: Record<string, OpenClawChannelConfig> | null | undefined
) {
  return Object.keys(channels ?? {}).length > 0;
}

export function hasConfiguredModels(models: BotModelsPayload | null | undefined) {
  if (!models) return false;

  return Boolean(
    models.active_model_type ||
      models.cloud_model_type ||
      (models.custom_providers?.length ?? 0) > 0 ||
      (models.local_providers?.length ?? 0) > 0
  );
}

export function summarizeSetup(
  channels: Record<string, OpenClawChannelConfig>,
  models: BotModelsPayload | null,
  botStatus: string
): OpenClawSetupSummary {
  const modelCount =
    (models?.cloud_model_type ? 1 : 0) +
    (models?.custom_providers?.length ?? 0) +
    (models?.local_providers?.length ?? 0);

  return {
    channelCount: Object.keys(channels).length,
    modelCount,
    botStatus,
  };
}

export function hasMeaningfulOpenClawSetup(
  channels: Record<string, OpenClawChannelConfig>,
  models: BotModelsPayload | null,
  botStatus: string
) {
  return hasConfiguredChannels(channels) || hasConfiguredModels(models) || botStatus === 'running';
}

export function sanitizeProviderId(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export function useOpenClawSetupStatus(): OpenClawSetupStatus {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channels, setChannels] = useState<Record<string, OpenClawChannelConfig>>({});
  const [botStatus, setBotStatus] = useState('stopped');
  const [models, setModels] = useState<BotModelsPayload | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [channelResponse, modelResponse] = await Promise.all([
        proxyFetchGet('/api/bot/channels/config'),
        proxyFetchGet('/api/bot/models'),
      ]);

      const nextChannels = (channelResponse?.channels ?? {}) as Record<string, OpenClawChannelConfig>;
      setChannels(nextChannels);
      setBotStatus(channelResponse?.bot_status ?? 'stopped');
      setModels((modelResponse?.models ?? null) as BotModelsPayload | null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load OpenClaw setup');
      setChannels({});
      setBotStatus('stopped');
      setModels(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const summary = summarizeSetup(channels, models, botStatus);

  return {
    loading,
    error,
    channels,
    botStatus,
    models,
    hasConfiguredChannels: hasConfiguredChannels(channels),
    hasConfiguredModels: hasConfiguredModels(models),
    hasMeaningfulSetup: hasMeaningfulOpenClawSetup(channels, models, botStatus),
    summary,
    refresh,
  };
}